import { useEffect, useRef, useState } from 'react'
import { Dialog } from './Dialog.js'
import { Button } from './Button.js'

interface CameraCaptureDialogProps {
  open: boolean
  onClose: () => void
  /** Called with the captured photo as a File, ready to upload. */
  onCapture: (file: File) => void
}

/**
 * In-app webcam/camera capture using getUserMedia — works on laptops AND mobile
 * (unlike <input capture>, which only hints the OS camera on phones and is
 * ignored on desktop). Requests the camera, shows a live preview, and grabs a
 * single frame to a JPEG File on "Capture".
 *
 * Requires a secure context (HTTPS) — brightstars.in qualifies; localhost does
 * too. If permission is denied or no camera exists, we surface a clear message
 * and the caller's "Choose from gallery" path remains available as a fallback.
 */
export function CameraCaptureDialog({ open, onClose, onCapture }: CameraCaptureDialogProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  const [busy, setBusy] = useState(false)

  // Fully release the camera — stopping every track turns off the OS "camera in
  // use" indicator. Called on close and on unmount.
  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    setReady(false)
  }

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setError(null)
    setReady(false)

    // getUserMedia is only present in a secure context with a camera.
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('This browser does not support in-app camera capture. Use "Choose from gallery" instead.')
      return
    }

    navigator.mediaDevices
      // Prefer the rear camera on phones; harmless on laptops (single cam).
      .getUserMedia({ video: { facingMode: 'environment' }, audio: false })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        streamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          setReady(true)
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const name = err instanceof DOMException ? err.name : ''
        setError(
          name === 'NotAllowedError'
            ? 'Camera permission was denied. Allow camera access in your browser, or use "Choose from gallery".'
            : name === 'NotFoundError'
              ? 'No camera was found on this device. Use "Choose from gallery" instead.'
              : 'Could not start the camera. Use "Choose from gallery" instead.',
        )
      })

    return () => {
      cancelled = true
      stopStream()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const capture = () => {
    const video = videoRef.current
    if (!video) return
    setBusy(true)
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      setError('Could not capture the photo. Try again.')
      setBusy(false)
      return
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    canvas.toBlob(
      (blob) => {
        setBusy(false)
        if (!blob) {
          setError('Could not capture the photo. Try again.')
          return
        }
        const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' })
        onCapture(file)
        onClose()
      },
      'image/jpeg',
      0.9,
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Take a photo"
      description="Position the student in frame, then capture."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={capture} disabled={!ready || busy} loading={busy}>
            Capture
          </Button>
        </>
      }
    >
      {error ? (
        <p className="rounded-md border border-danger-border bg-danger-tint px-3 py-2 text-sm text-danger">
          {error}
        </p>
      ) : (
        <div className="overflow-hidden rounded-md border border-border bg-black">
          {/* muted + playsInline so mobile browsers autoplay the preview inline. */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="h-auto w-full"
          />
          {!ready ? (
            <p className="px-3 py-2 text-center text-sm text-fg-muted">Starting camera…</p>
          ) : null}
        </div>
      )}
    </Dialog>
  )
}
